import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  Modal,
  Platform,
  Dimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../../types/navigation';
import { useAuth } from '../../hooks/useAuth';
import { useTour } from '../../context/TourContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from '../../components/common/Button';
import { useTheme } from '../../hooks/useTheme';
import { trackButtonClick } from '../../utils/analytics';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import { SubscriptionBanner } from '../../components/shared/SubscriptionBanner';
import SupportForm from '../../components/shared/SupportForm';

interface SettingsSectionProps {
  title: string;
  children: React.ReactNode;
}

const SettingsSection = ({ title, children }: SettingsSectionProps) => {
  const { colors } = useTheme();

  return (
    <View style={styles.section}>
      <Text style={[styles.sectionTitle, { color: colors.primary }]}>
        {title}
      </Text>
      <View style={styles.sectionContent}>{children}</View>
    </View>
  );
};

interface SettingsItemProps {
  icon: string;
  title: string;
  subtitle?: string;
  onPress?: () => void;
  rightElement?: React.ReactNode;
}

const SettingsItem = ({
  icon,
  title,
  subtitle,
  onPress,
  rightElement,
}: SettingsItemProps) => {
  const { colors } = useTheme();

  return (
    <TouchableOpacity
      style={[
        styles.settingItem,
        { backgroundColor: colors.card, borderColor: colors.border },
      ]}
      onPress={onPress}
      disabled={!onPress}
    >
      <View style={styles.settingContent}>
        <Ionicons
          name={icon as keyof typeof Ionicons.glyphMap}
          size={22}
          color={colors.primary}
        />
        <View style={styles.settingTextContainer}>
          <Text style={[styles.settingText, { color: colors.text }]}>
            {title}
          </Text>
          {subtitle && (
            <Text style={[styles.settingSubtitle, { color: colors.secondary }]}>
              {subtitle}
            </Text>
          )}
        </View>
      </View>
      {rightElement ||
        (onPress && (
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        ))}
    </TouchableOpacity>
  );
};

const SettingsScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { signOut, isLoading, user } = useAuth();
  const { startTour } = useTour();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const [supportModalVisible, setSupportModalVisible] = useState(false);
  const [aboutModalVisible, setAboutModalVisible] = useState(false);

  const screenWidth = Dimensions.get('window').width;
  const isWebLayout = Platform.OS === 'web' && screenWidth >= 768;

  const handleSignOut = async () => {
    await signOut();
    // Navigation to Login screen is handled in the signOut function
  };

  const renderSettingsContent = () => (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[
        styles.scrollViewContent,
        {
          paddingBottom: 40 + insets.bottom,
          paddingHorizontal: isWebLayout ? 24 : 16,
          paddingTop: isWebLayout ? 24 : 16,
        },
      ]}
      showsVerticalScrollIndicator={false}
    >
      {/* Subscription Management */}
      <SubscriptionBanner
        style={{ marginBottom: 24 }}
        navigateToPricing={true}
      />

      <SettingsSection title="ACCOUNT">
        <SettingsItem
          icon="mail-outline"
          title="Email"
          subtitle={user?.email || 'Not set'}
        />
        <SettingsItem
          icon="lock-closed-outline"
          title="Change Password"
          subtitle="Update your password"
          onPress={() => {
            // For mobile, navigate to the dedicated change password screen
            navigation.navigate('ChangePassword');
          }}
        />
      </SettingsSection>

      <SettingsSection title="APP">
        <SettingsItem
          icon="information-circle-outline"
          title="About ColourSense"
          subtitle="Version 1.0.0"
          onPress={() => setAboutModalVisible(true)}
        />
        <SettingsItem
          icon="help-circle-outline"
          title="Help & Support"
          subtitle="FAQ and contact support"
          onPress={() => {
            navigation.navigate('HelpCenter');
          }}
        />
        <SettingsItem
          icon="play-circle-outline"
          title="Start Tutorial"
          subtitle="Run the guided tour again"
          onPress={() => {
            trackButtonClick('Start Tutorial', 'settings-screen', {
              button_type: 'start_tutorial',
            });
            startTour();
            navigation.navigate('MainTabs', {
              section: 'home',
              initialTab: 'HomeTab',
            });
          }}
        />
        <SettingsItem
          icon="mail-outline"
          title="Contact Support"
          subtitle="Get help from our support team"
          onPress={() => {
            setSupportModalVisible(true);
          }}
        />
        <SettingsItem
          icon="shield-checkmark-outline"
          title="Privacy Policy"
          subtitle="View privacy policy"
          onPress={() => {
            navigation.navigate('PrivacyPolicy');
          }}
        />
        <SettingsItem
          icon="document-text-outline"
          title="Terms of Service"
          subtitle="View terms of service"
          onPress={() => {
            navigation.navigate('TermsOfService');
          }}
        />
      </SettingsSection>

      <View style={styles.logoutContainer}>
        <Button
          title="Sign Out"
          onPress={handleSignOut}
          variant="outline"
          loading={isLoading}
        />
      </View>

      <SupportForm
        visible={supportModalVisible}
        onClose={() => setSupportModalVisible(false)}
        onSuccess={() => {
          Alert.alert(
            'Success',
            'Your support request has been submitted. We will get back to you within 24 hours.'
          );
        }}
      />

      <Modal
        visible={aboutModalVisible}
        animationType="slide"
        transparent
        onRequestClose={() => setAboutModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.aboutModalContent,
              { backgroundColor: colors.background },
            ]}
          >
            <View
              style={[
                styles.aboutModalHeader,
                { borderBottomColor: colors.border },
              ]}
            >
              <Text style={[styles.aboutModalTitle, { color: colors.text }]}>
                About ColourSense
              </Text>
              <TouchableOpacity
                onPress={() => setAboutModalVisible(false)}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                style={styles.aboutModalClose}
              >
                <Ionicons name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>
            <ScrollView
              style={styles.aboutModalScroll}
              contentContainerStyle={styles.aboutModalScrollContent}
              showsVerticalScrollIndicator={true}
            >
              <View
                style={[
                  styles.aboutDetailSection,
                  { borderBottomColor: colors.border },
                ]}
              >
                <Text
                  style={[
                    styles.aboutDetailSubtitle,
                    { color: colors.secondary },
                  ]}
                >
                  Version
                </Text>
                <Text style={[styles.aboutDetailValue, { color: colors.text }]}>
                  1.0.0
                </Text>
              </View>
              <View
                style={[
                  styles.aboutDetailSection,
                  { borderBottomColor: colors.border },
                ]}
              >
                <Text
                  style={[
                    styles.aboutDetailSubtitle,
                    { color: colors.secondary },
                  ]}
                >
                  Description
                </Text>
                <Text style={[styles.aboutDetailValue, { color: colors.text }]}>
                  A smart colour analysis app that helps you discover your
                  perfect colors with AI-powered analysis, virtual draping, and
                  colour generation.
                </Text>
              </View>
              <View
                style={[
                  styles.aboutDetailSection,
                  { borderBottomColor: colors.border },
                ]}
              >
                <Text
                  style={[
                    styles.aboutDetailSubtitle,
                    { color: colors.secondary },
                  ]}
                >
                  Features
                </Text>
                <Text style={[styles.aboutDetailValue, { color: colors.text }]}>
                  • AI-powered colour analysis{'\n'}• Virtual draping{'\n'}•
                  Colour Generation
                </Text>
              </View>
              <View
                style={[
                  styles.aboutDetailSection,
                  { borderBottomColor: colors.border },
                ]}
              >
                <Text
                  style={[
                    styles.aboutDetailSubtitle,
                    { color: colors.secondary },
                  ]}
                >
                  Last Updated
                </Text>
                <Text style={[styles.aboutDetailValue, { color: colors.text }]}>
                  February 2026
                </Text>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );

  // For web, we don't render this screen directly - it's handled by the sidebar
  if (isWebLayout) {
    return null;
  }

  return (
    <ResponsiveContainer style={{ backgroundColor: colors.border }}>
      {renderSettingsContent()}
    </ResponsiveContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  section: {
    marginBottom: 18,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 6,
    marginLeft: 8,
  },
  sectionContent: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
  },
  settingContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  settingTextContainer: {
    marginLeft: 12,
    flex: 1,
  },
  settingText: {
    fontSize: 16,
    fontWeight: '500',
  },
  settingSubtitle: {
    fontSize: 14,
    marginTop: 2,
  },
  logoutContainer: {
    padding: 24,
    marginTop: 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  aboutModalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    minHeight: 300,
  },
  aboutModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  aboutModalTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  aboutModalClose: {
    padding: 4,
  },
  aboutModalScroll: {
    maxHeight: 400,
  },
  aboutModalScrollContent: {
    padding: 20,
    paddingBottom: 32,
  },
  aboutDetailSection: {
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  aboutDetailSubtitle: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 6,
  },
  aboutDetailValue: {
    fontSize: 15,
    lineHeight: 22,
  },
});

export default SettingsScreen;
